/**
 * Ответы для блока «Доходы» по документам клиента — для общего экрана
 * загрузки в «Должнике» (решение 28.09.2026).
 *
 * В самом блоке «Доходы» у каждого документа своя кнопка: документы отвечают
 * на разные вопросы. На общем экране те же ответы собраны в группы — работа,
 * зарплата, ИП, выплаты, — и в каждой группе отмечено не больше одного.
 * Отмечается только очевидное:
 *
 *   • работа — по сведениям о трудовой деятельности: основное место или «не
 *     трудоустроен»;
 *   • зарплата — по лицевому счёту СФР у работодателя основного места; если
 *     его нет — по 2-НДФЛ того же работодателя без признака увольнения.
 *     Работодатель не совпал с местом работы — ответы есть, но без отметки;
 *   • ИП — по справке ЕГРИП, если ИНН в ней не чужой;
 *   • выплаты — только текущие по ЕГИССО (ответ юриста 27.09.2026, вопрос 17)
 *     и пенсии по справке СФР; ничего текущего — «выплат нет».
 */

import type { Income } from '@bd/domain';
import type { IncomeImport } from '../../../api.js';

export type IncomeGroup = 'employment' | 'salary' | 'entrepreneur' | 'social';

export interface IncomeSuggestion {
  id: string;
  group: IncomeGroup;
  /** Что подставится — словами */
  label: string;
  /** Откуда взято */
  source: string;
  patch: Partial<Income>;
  checked: boolean;
}

export const GROUP_TITLES: Record<IncomeGroup, string> = {
  employment: 'Работа',
  salary: 'Средняя зарплата',
  entrepreneur: 'Индивидуальный предприниматель',
  social: 'Пособия и выплаты',
};

type Of<K extends IncomeImport['kind']> = Extract<IncomeImport, { kind: K }>;

const rub = (kopecks: number) => (kopecks / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Одно и то же ли учреждение: «БУ "НЕФТЕЮГАНСКАЯ ОКБ…"» и «БЮДЖЕТНОЕ УЧРЕЖДЕНИЕ … "НЕФТЕЮГАНСКАЯ ОКБ…"». */
function sameEmployer(a: string, b: string): boolean {
  const quoted = (name: string) => (/["«]([^"»]+)/.exec(name)?.[1] ?? name).toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]/gu, '');
  const [x, y] = [quoted(a), quoted(b)];
  return Boolean(x && y) && (x.includes(y) || y.includes(x));
}

export function incomeSuggestions(results: IncomeImport[], debtorInn: string): IncomeSuggestion[] {
  const of = <K extends IncomeImport['kind']>(kind: K) => results.filter((r): r is Of<K> => r.kind === kind);
  const list: IncomeSuggestion[] = [];
  const debtor = debtorInn.replace(/\s/g, '');

  // Работа
  const work = of('work-history')[0];
  const main = work ? (work.currentJobs.find((j) => !j.partTime) ?? work.currentJobs[0]) : undefined;
  if (work) {
    list.push(
      main
        ? { id: 'work', group: 'employment', label: `Трудоустроен: ${main.employer}`, source: work.title, patch: { employed: true, employerName: main.employer }, checked: true }
        : { id: 'work', group: 'employment', label: 'Не трудоустроен', source: work.title, patch: { employed: false }, checked: true },
    );
  }

  // Зарплата: сначала лицевой счёт, потом 2-НДФЛ; отмечается только у работодателя основного места
  const ils = of('ils')[0];
  if (ils) {
    const latest = ils.employers[0]?.year ?? 0;
    ils.employers
      .filter((e) => (e.year ?? 0) >= latest - 1 && e.monthly > 0)
      .forEach((e, i) =>
        list.push({
          id: `ils-${i}`,
          group: 'salary',
          label: `${rub(e.monthly)} ₽ в месяц — ${e.employer}`,
          source: `${ils.title}, ${e.lastPeriod.label}, до вычета налога`,
          patch: {
            monthlySalary: e.monthly,
            salaryConfirmation: 'Сведения о состоянии индивидуального лицевого счёта застрахованного лица',
          },
          checked: false,
        }),
      );
  }
  const latestNdfl = of('ndfl')
    .flatMap((r) => r.certificates)
    .filter((c) => c.averageSalary > 0)
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  latestNdfl.forEach((c, i) =>
    list.push({
      id: `ndfl-${i}`,
      group: 'salary',
      label: `${rub(c.averageSalary)} ₽ в месяц — ${c.agentName}`,
      source: `Справка 2-НДФЛ${c.year ? ` за ${c.year} год` : ''}${c.looksLikeDismissal ? ', есть компенсация за отпуск — похоже на увольнение' : ''}`,
      patch: {
        monthlySalary: c.averageSalary,
        employerInn: c.agentInn ?? '',
        salaryConfirmation: `Справка о доходах и суммах налога физического лица${c.year ? ` за ${c.year} год` : ''}`,
      },
      checked: false,
    }),
  );
  if (main) {
    const salary = list.filter((s) => s.group === 'salary');
    const pick =
      salary.find((s) => s.id.startsWith('ils') && sameEmployer(s.label.split(' — ')[1] ?? '', main.employer)) ??
      salary.find((s) => s.id.startsWith('ndfl') && !s.source.includes('увольнение') && sameEmployer(s.label.split(' — ')[1] ?? '', main.employer));
    if (pick) pick.checked = true;
  }

  // ИП
  const egrip = of('egrip')[0];
  if (egrip && egrip.isEntrepreneur !== null) {
    const foreign = Boolean(egrip.inn && debtor && egrip.inn !== debtor);
    list.push(
      egrip.isEntrepreneur
        ? { id: 'egrip', group: 'entrepreneur', label: `Индивидуальный предприниматель, ОГРНИП ${egrip.ogrnip ?? '—'}`, source: egrip.title, patch: { isEntrepreneur: true, entrepreneurOgrnip: egrip.ogrnip ?? '' }, checked: !foreign }
        : { id: 'egrip', group: 'entrepreneur', label: 'Не является индивидуальным предпринимателем', source: egrip.title, patch: { isEntrepreneur: false, entrepreneurOgrnip: '' }, checked: !foreign },
    );
  }

  // Выплаты: только текущие
  const egisso = of('egisso');
  const sfr = of('sfr-pension');
  const current = [
    ...egisso.filter((r) => r.measures.some((m) => m.current) && r.description).map((r) => ({ text: r.description, source: r.title })),
    ...sfr.filter((r) => r.isRecipient && r.details).map((r) => ({ text: r.details, source: r.title })),
  ];
  if (current.length) {
    list.push({
      id: 'social',
      group: 'social',
      label: `Получает: ${current.map((c) => c.text).join('; ')}`,
      source: current.map((c) => c.source).join('; '),
      patch: { hasSocialPayments: true, socialPaymentsDescription: current.map((c) => c.text).join('; ') },
      checked: true,
    });
  } else if (egisso.length || sfr.length) {
    list.push({
      id: 'social',
      group: 'social',
      label: 'Текущих пособий и выплат нет',
      source: [...egisso, ...sfr].map((r) => r.title).join('; '),
      patch: { hasSocialPayments: false, socialPaymentsDescription: '' },
      checked: true,
    });
  }

  return list;
}

/** Отмеченные ответы — одним исправлением блока «Доходы». */
export function mergeIncomePatches(suggestions: IncomeSuggestion[]): Partial<Income> {
  return suggestions.filter((s) => s.checked).reduce<Partial<Income>>((patch, s) => ({ ...patch, ...s.patch }), {});
}
